import { ListFilter } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListSubheader from "@mui/material/ListSubheader";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { Fragment, useState } from "react";
import SearchField from "../SearchField.tsx";
import type { FilterSpec } from "./specs.ts";

const SEARCH_THRESHOLD = 8;

interface AddFilterMenuProps {
  specs: FilterSpec[];
  onPick: (key: string) => void;
}

/** "+ Filter": the filters not shown as pills yet, grouped, searchable when many. */
export default function AddFilterMenu({ specs, onPick }: AddFilterMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  if (specs.length === 0) return null;

  const q = query.trim().toLowerCase();
  const shown = q ? specs.filter((s) => s.label.toLowerCase().includes(q)) : specs;
  const groups: [string, FilterSpec[]][] = [];
  for (const s of shown) {
    const name = s.group ?? "";
    const group = groups.find(([g]) => g === name);
    if (group) group[1].push(s);
    else groups.push([name, [s]]);
  }

  const close = () => {
    setAnchor(null);
    setQuery("");
  };

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<ListFilter />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        sx={{ color: "text.secondary", height: 32, borderRadius: 999, px: 1.5, "&:hover": { color: "text.primary" } }}
      >
        Filter
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.75, width: 260, borderRadius: 2 } } }}
      >
        {specs.length > SEARCH_THRESHOLD && (
          <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
            <SearchField value={query} onChange={setQuery} placeholder="Filter by…" autoFocus fullWidth />
          </Box>
        )}
        <List dense disablePadding sx={{ maxHeight: 380, overflowY: "auto", py: 0.5 }} role="menu">
          {groups.map(([group, items]) => (
            <Fragment key={group || "_"}>
              {group && (
                <ListSubheader disableSticky sx={{ lineHeight: "28px", px: 1.5, typography: "overline", color: "text.secondary" }}>
                  {group}
                </ListSubheader>
              )}
              {items.map((s) => (
                <ListItemButton
                  key={s.key}
                  role="menuitem"
                  onClick={() => {
                    close();
                    onPick(s.key);
                  }}
                  sx={{ px: 1.5, minHeight: 32 }}
                >
                  <Typography variant="body2">{s.label}</Typography>
                </ListItemButton>
              ))}
            </Fragment>
          ))}
          {shown.length === 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary", px: 1.5, py: 1.5 }}>
              No filter matches
            </Typography>
          )}
        </List>
      </Popover>
    </>
  );
}
